import moment from "moment";
import type { BotWritingState } from "../Helper/KaiAgentState";
import { isSettledWidgetAnswer } from "../Helper/SettledWidgetAnswer";
import { create } from "zustand";
import { defaultAttachmentMessage } from "../Components/ChatMessageComposer/ChatMessageComposer";
import {
  clearActiveChatbarTicket,
  getActiveChatbarTicket,
  isReopenableStatus,
  saveActiveChatbarTicket,
} from "../Helper/ChatbarActiveTicketCache";
import CommunicationManager from "../Helper/CommunicationManager";
import Communicator from "../Helper/Communicator";
import {
  CONVERSATION_ATTACHMENT_LIMIT_BYTES,
  CONVERSATION_ATTACHMENT_SIZE_ERROR_MESSAGE,
} from "../Helper/ConversationAttachmentUpload";
import { EffectiveHoursConfig } from "../Helper/EffectiveHoursConfig";
import { validateRecaptchaAction } from "../Helper/Recaptcha";
import { sanitizeHtmlHelper } from "../Helper/SanitizeHtmlHelper";
import {
  WebSocketEvent,
  WebSocketEventData,
} from "../Models/WebSocketEventData";
import { commentEndsBotTurn, snapshotEndsBotTurn } from "../Helper/BotTurn";
import {
  isCaptureLogsNotice,
  mergeCaptureCommentUpdate,
} from "../Helper/CaptureRequest";
import { isBotTeamReply } from "../Helper/MessageAuthor";
import {
  applyAnswerSendOutcome,
  classifyAnswerSendError,
  dropSendingLocalsFor,
  mergeRefetchedComments,
  type AnswerSendErrorReason,
} from "../Helper/OptimisticComments";
import {
  MISSING_ACTION_REFETCH_DELAYS_MS,
  shouldRefetchForMissingAction,
  withCollectedAnswerText,
} from "../Helper/CollectedAnswer";
import { HttpClientXHR } from "../Services/http.clients/config/http.client";
import { HttpMessageService } from "../Services/http.clients/http.message.client";
import { HttpTicketService } from "../Services/http.clients/http.ticket.client";
import { useAppViewStore } from "./AppView";
import { useConfigStore } from "./Config";
import { useFormStore } from "./Form";
import { AppMode, useRouterStore } from "./Router";
import { useSessionStore } from "./Session";
import {
  isVoiceCallBusy,
  runOrDeferUntilCallOver,
  shouldMuteWidgetSounds,
} from "./VoiceCallActivity";

// How long we wait for the host SDK to answer `collect-ticket-data`. See
// getTicketData for why app mode needs so much more headroom than the web.
const COLLECT_TICKET_DATA_TIMEOUT_WEB = 800;
const COLLECT_TICKET_DATA_TIMEOUT_APP = 3000;

// Timestamp of the last "switch to existing ticket" redirect (BOT_OPENTICKET).
// A workflow can redirect the customer to an existing conversation right as a new
// one is being created; if that event lands first, createNewConversation must not
// navigate back to the (now archived) new conversation.
var redirectedToShareTokenAt = 0;

// The server treats vs=hidden fetches as "not actually reading": they neither
// mark the conversation read nor cancel pending fallback notifications.
const getVisibilityState = () =>
  document.visibilityState === "hidden" ? "hidden" : "visible";

// Safety net for the "Thinking…" / streaming indicator.
//
// The indicator is only ever cleared by an incoming event (the bot's final
// message, an agent reply, a comment refetch). If that terminal event never
// arrives — the AI run dies mid-turn, the worker restarts, the socket drops the
// message — the indicator stays up forever, and because the composer disables
// its send button while the bot is writing, the customer is locked out of the
// conversation with no way to recover but a reload.
//
// Every BOT_WRITTING event re-arms this timer, so it can only fire after a full
// idle window with no sign of life from the bot (the server throttles partial
// updates to 800ms and emits a status update per tool step, so a healthy turn —
// however long — never goes quiet this long).
const BOT_WRITTING_IDLE_TIMEOUT_MS = 120000;
// Well before the drop-everything watchdog above: a stream that goes quiet
// mid-bubble usually means the turn's terminal MESSAGE_CREATED was lost (WS
// hiccup / server restart between the last chunk and the comment insert).
// The composer is locked the whole time botWrittingText is set, so waiting
// the full 120s strands the customer under an already-visible answer. A
// silent refetch is the cheap recovery: loadComments stages the saved reply
// for the typewriter (or changes nothing when the server is genuinely still
// working — it throttles partials to 800ms, so a healthy turn re-arms this
// long before it fires).
const BOT_WRITTING_IDLE_REFETCH_MS = 10000;
/** A comment timestamp as epoch ms, or undefined when missing / unparsable. */
const toTime = (value: unknown): number | undefined => {
  if (!value) return undefined;
  const time = new Date(value as any).getTime();
  return Number.isFinite(time) ? time : undefined;
};
var botWrittingWatchdog: ReturnType<typeof setTimeout> | undefined;
var botWrittingIdleRefetch: ReturnType<typeof setTimeout> | undefined;

const clearBotWrittingWatchdog = () => {
  if (botWrittingWatchdog) {
    clearTimeout(botWrittingWatchdog);
    botWrittingWatchdog = undefined;
  }
  if (botWrittingIdleRefetch) {
    clearTimeout(botWrittingIdleRefetch);
    botWrittingIdleRefetch = undefined;
  }
};

const armBotWrittingWatchdog = (
  get: () => TicketState,
  set: (state: Partial<TicketState>) => void,
) => {
  clearBotWrittingWatchdog();
  botWrittingIdleRefetch = setTimeout(() => {
    botWrittingIdleRefetch = undefined;
    // The typewriter owns a staged reply; and a bubble that already cleared
    // needs no recovery.
    if (get().pendingFinalComment) {
      return;
    }
    if (!get().botWrittingText && !get().botWrittingIsThinking) {
      return;
    }
    get()
      .loadComments(true)
      .catch(() => {});
  }, BOT_WRITTING_IDLE_REFETCH_MS);
  botWrittingWatchdog = setTimeout(() => {
    botWrittingWatchdog = undefined;
    // A reply staged for the typewriter is mid-commit — leave it alone.
    if (get().pendingFinalComment) {
      return;
    }
    if (!get().botWrittingText && !get().botWrittingIsThinking) {
      return;
    }
    set({ botWrittingText: undefined, botWrittingIsThinking: false, botWrittingState: undefined });
  }, BOT_WRITTING_IDLE_TIMEOUT_MS);
};

// The awaiting workflow step (buttons / chatinput / …) reaches the widget as a
// hidden-action BOT comment over WS. That event is dropped while the
// conversation is still loading or the widget is closed, and the initial
// comment fetch can race the workflow's first step (it lands ~1s after the
// conversation is created) — the customer is then stuck on "typing" with no
// buttons, and a later composer send is mis-shaped (#144422). While a live bot
// conversation shows no awaiting step, silently re-fetch a few times.
let missingActionRefetchTimers: ReturnType<typeof setTimeout>[] = [];
const cancelMissingActionRefetch = () => {
  missingActionRefetchTimers.forEach((t) => clearTimeout(t));
  missingActionRefetchTimers = [];
};
const armMissingActionRefetch = (get: () => TicketState) => {
  cancelMissingActionRefetch();
  const shareToken = get().currentTicket?.shareToken;
  if (!shareToken) return;
  const stillMissing = () => {
    const state = get();
    if (state.currentTicket?.shareToken !== shareToken) return false;
    return shouldRefetchForMissingAction({
      botActive: state.currentTicket?.bot?.active,
      conversationClosed: state.currentTicket?.conversationClosed,
      hasAction: !!state.currentBotAction?.action,
      hasAnswerBotReply: state.hasAnswerBotReply(),
    });
  };
  if (!stillMissing()) return;
  missingActionRefetchTimers = MISSING_ACTION_REFETCH_DELAYS_MS.map((delay) =>
    setTimeout(() => {
      if (!stillMissing()) {
        cancelMissingActionRefetch();
        return;
      }
      get()
        .loadComments(true)
        .catch(() => {});
    }, delay),
  );
};

// Apply a newly-created comment (from a MESSAGE_CREATED WS event).
//
//   • AI reply mid-stream → stash for the typewriter to commit.
//   • Otherwise          → append, clearing the streaming bubble when the
//                          comment ends the bot's turn (`commentEndsBotTurn`).
//
// This helper also owns clearing `typingUser`: the typing indicator must
// survive until its message is actually ON SCREEN, which is only after the
// findOne round-trip below resolves. Clearing it on the WS event itself (as
// the MESSAGE_CREATED handler used to) left a blank beat — no indicator, no
// message — for exactly that round-trip, stacking up when a bot turn creates
// several comments back-to-back (e.g. Kai's reply + the offline notice on a
// forward-to-support).
const applyCreatedComment = async (
  commentId: string,
  parentId: string,
  get: () => TicketState,
  set: (state: Partial<TicketState>) => void,
) => {
  const ticket = get().currentTicket;
  if (!ticket) return;

  let fetched: TicketChatMessage;
  try {
    fetched = await HttpMessageService.getInstance().findOne({
      id: commentId,
      query: {
        ticketShareToken: ticket.shareToken,
        vs: getVisibilityState(),
      },
    });
  } catch (_) {
    // Nothing will render for this event — don't strand the indicators.
    get().clearUserTyping();
    if (!get().pendingFinalComment) {
      set({ botWrittingText: undefined, botWrittingIsThinking: false, botWrittingState: undefined });
    }
    return;
  }
  if (get().currentTicket?.id !== parentId) return;

  // The customer is watching this conversation — keep the local unread state
  // (card dot, Messages badge) in sync. The fetch above already acknowledged
  // the read server-side.
  if (document.visibilityState !== "hidden") {
    get().setTicketUnread(false);
  }

  const isBotMessage = fetched?.type === "BOT";
  const isAIReply = isBotMessage && fetched?.data?.answerBot === true;
  const isStreaming =
    !!get().botWrittingText && (get().botWrittingText as string).length > 0;
  const pendingAIReply = !!get().pendingFinalComment;

  // 1. AI reply mid-stream → defer until the typewriter finishes.
  if (isAIReply && isStreaming) {
    get().clearUserTyping();
    set({ canSetBotWrittingText: false, pendingFinalComment: fetched });
    return;
  }

  // 2. Append, clearing the indicator when this comment ends the bot's turn.
  const endsBotTurn = commentEndsBotTurn(fetched, pendingAIReply);
  // A customer reply (BOT_REPLY) arriving over WS is a server-delivered message
  // — e.g. the agentic-chatbar question. The client never went through
  // answerBotAction, so re-arm the "Thinking…" indicator the way a normal send
  // would; otherwise the AI's BOT_WRITTING events stay gated out (the preceding
  // answerbotflow greeting comment just reset canSetBotWrittingText to false).
  const isCustomerReply = fetched?.type === "BOT_REPLY";
  // Only the OTHER side's message says anything about the other side's typing
  // state. The customer's own echo (BOT_REPLY / SHARED_COMMENT / …) must not
  // kill the bot/agent typing indicator — the reply it announces is still on
  // its way. Neither does the diagnostic-logs notice (a system line).
  if (
    !isCaptureLogsNotice(fetched) &&
    (isBotMessage || !!fetched?.user || isBotTeamReply(fetched))
  ) {
    get().clearUserTyping();
  }
  // Only the customer echo may drop in-flight locals — a bot/agent message
  // landing while the customer's POST is still open must keep their bubble.
  const list = dropSendingLocalsFor(get().currentComments, fetched);
  const next = list.some((c) => c.id === fetched?.id)
    ? list
    : [...list, fetched];

  get().setCurrentComments(
    next,
    endsBotTurn
      ? {
          canSetBotWrittingText: false,
          botWrittingText: undefined,
          botWrittingIsThinking: false, botWrittingState: undefined,
          pendingFinalComment: undefined,
          botTurnEndedAt: Date.now(),
        }
      : isCustomerReply
        ? { canSetBotWrittingText: true }
        : undefined,
  );
};

export const getRandomMessageId = () => {
  return new Date().toISOString() + Math.random().toString(36).substring(2, 15);
};

// One id per customer message, sent with it and reused when the HTTP client
// retries the send: the server stores the message once (a timed-out send the
// server had finished came back as a second copy).
export const newClientMessageId = () => {
  const random =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID().replace(/-/g, "")
      : `${Date.now().toString(36)}${Math.random().toString(36).substring(2, 14)}`;
  return `cm_${random}`;
};

export const getLastUpdateDate = (ticket: Ticket) => {
  if (ticket.latestPublicComment) {
    return moment(ticket.latestPublicComment.createdAt);
  }

  return moment(ticket.createdAt);
};

const cleanConversationList = (newTickets: Ticket[]) => {
  // Sort tickets by updatedAt or createdAt as fallback.
  newTickets = newTickets.sort((a, b) => {
    const dateA = getLastUpdateDate(a);
    const dateB = getLastUpdateDate(b);

    return dateB.diff(dateA); // Sort in descending order (newest first)
  });

  // Remove duplicates.
  newTickets = newTickets.filter(
    (v, i, a) => a.findIndex((v2) => v2.id === v.id) === i,
  );

  return newTickets;
};

export enum ChannelTypes {
  EMAIL = "email",
  WHATSAPP = "whatsapp",
  INSTAGRAM = "instagram",
  FACEBOOK = "facebook",
  WIDGET = "widget",
}

export type Ticket = {
  id?: string;
  title?: string;
  bugId?: string;
  createdAt?: Date;
  updatedAt?: Date;
  noHumanInteraction?: boolean;
  form?: any;
  formData?: any;
  type?: string;
  status?: string;
  screenshotUrl?: string;
  screenRecordingUrl?: string;
  hidden?: boolean;
  hideContent?: boolean;
  conversationClosed?: boolean;
  generatingScreenshot?: boolean;
  sessionNotificationsUnread?: boolean;
  shareToken?: string;
  upvotesCount?: number;
  upvoted?: boolean;
  session?: {
    name: string;
    gleapId: string;
  };
  bot?: {
    active: boolean;
    // Workflow attached to the conversation; null for direct-agent
    // conversations (e.g. started from the AI chatbar).
    currentBot?: string | null;
  };
  processingUser?: {
    firstName?: string;
    lastName?: string;
    email?: string;
    id?: string;
    profileImageUrl?: string;
  };
  latestPublicComment?: {
    data?: any;
    // The WS-pushed shape ships a flat plain-text preview here instead of
    // a full data.content, so it's optional and may be set on the WS
    // payload while data.* is undefined.
    text?: string;
    type: string;
    createdAt: Date;
    updatedAt?: Date;
    user?: {
      firstName?: string;
      lastName?: string;
      email?: string;
      id?: string;
      profileImageUrl?: string;
    };
    attachments?: any[];
    session?: {
      name: string;
      gleapId: string;
    };
    fallbackUser?: {
      username: string;
    };
  };
  channel: {
    id: string;
    type: ChannelTypes;
  };
  /**
   * Server-resolved, language-localized hours config for this conversation's
   * assigned team (with project fallback). Used by ChatMessage and
   * ConversationItem to render team-aware reply-time / online state.
   */
  effectiveHoursConfig?: EffectiveHoursConfig;
};

export type TicketChatMessage = {
  type?: string;
  user?: {
    email?: string;
    firstName?: string;
    lastName?: string;
    id?: string;
    profileImageUrl?: string;
  };
  session?: {
    gleapId?: string;
    email?: string;
    name?: string;
    userId?: string;
    id?: string;
  };
  data?: any;
  attachments?: any[];
  createdAt?: Date;
  updatedAt?: Date;
  // Client-only: the `updatedAt` of the last card (data.widget) update applied.
  widgetUpdatedAt?: Date | string;
  id?: string;
  sending?: boolean;
  error?: boolean;
  // Optional copy for the errored state (e.g. "conversation ended" on a 409);
  // the bubble falls back to the generic network-error text.
  errorMessage?: string;
  sessionNotificationsUnread?: boolean;
  kaiChat?: boolean;
  // Server-authored without a teammate (e.g. Kai's own account via MCP).
  bot?: boolean;
  rating?: "positive" | "negative";
  // Quoted original message (populated by the server on read).
  replyTo?: TicketChatMessage | null;
};

export type AnswerBotActionResult =
  | { sent: true }
  | { sent: false; reason: AnswerSendErrorReason };

// The answer POST runs the workflow step / wakes the agent before it responds
// and, after a long idle, also pays for a cold connection — the 4s client
// default aborted exactly those sends (#146330). Never retried: an aborted
// answer is usually still processed server-side, so a retry would double the
// customer's message; the errored bubble is the retry.
const ANSWER_WORKFLOW_TIMEOUT_MS = 30000;

interface TicketState {
  previousTickets: Ticket[];
  previousTicketsLoaded: boolean;
  chatbarListUnlocked: boolean;
  ticketFilter: Record<string, any>;
  loadingTickets?: boolean;
  canLoadMore?: boolean;
  currentTicket?: Ticket;
  typingUser: any;
  typingSource?: "local" | "server";
  /**
   * Per-message agent identity of a server typing event (same fields the
   * settled reply ships in `data`): when set, the typing row renders as the
   * agent's own author group instead of the generic bot/person shape.
   */
  typingAgent?: { agentName?: string; agentAvatarUrl?: string };
  botWrittingText?: string;
  botWrittingIsThinking?: boolean;
  /** What Kai is doing behind the status line (server `state` on bot.writting). */
  botWrittingState?: BotWritingState;
  /** `Date.now()` of the last bot.tool event — the avatar shows the cog briefly. */
  botToolAt?: number;
  /** `Date.now()` when the bot's turn ended — the avatar shows the check briefly. */
  botTurnEndedAt?: number;
  canSetBotWrittingText?: boolean;
  pendingFinalComment?: TicketChatMessage;
  currentBotAction?: {
    action: any;
    botId: string;
    actionFlowId: string;
    actionId: number;
    lastActionSent: Date;
  };
  uploadingAttachment?: boolean;
  uploadProgress: number[];
  userTypingTimeout: any;
  currentComments: TicketChatMessage[];
  loadingComments?: boolean;
  conversationError?: string;
  currentComposerText?: string;
  unreadTickets: Ticket[];
  // ── Home ask-input → conversation takeoff ──
  // The question typed into the home ask-input, mirrored while the
  // conversation is created so the loading shell and the conversation can
  // render it before the server delivers the real comment. Cleared by the
  // conversation once the customer's own comment has landed.
  pendingAiQuestionText?: string;
  // Timestamp for the mirrored bubble — close enough to the real comment's
  // createdAt that the swap doesn't change the rendered time label.
  pendingAiQuestionCreatedAt?: Date;
  // The conversation being created is picked up by a workflow (started for
  // one, or the project starts new conversations with one): the header shows
  // the bot while the ticket loads. Cleared once that ticket has loaded.
  pendingConversationIsBot: boolean;
  // True while the home composer is animating down into its conversation
  // position; the home content and tab bar fade out during this window.
  aiTakeoffActive?: boolean;
  // First message (text + attachments / audio) typed into the home composer
  // when it carries an attachment. createNewConversation stashes it here
  // instead of seeding it server-side; the conversation view delivers it once
  // live via the normal send path so the AI sees it as one message. Cleared
  // after it is consumed (and in the error path / on conversation teardown).
  pendingComposerMessage?: {
    text: string;
    attachments: { url: string; name: string; type: string }[];
  };
  loadMoreConversations: (arg?: {
    preventLoading?: boolean;
    skip?: number;
    // Refresh mode: the fetched page REPLACES the kept (stale) list instead
    // of merging — used by loadConversations' stale-while-revalidate reload.
    replace?: boolean;
  }) => void;
  setCurrentComposerText: (text: string) => void;
  setTicketUnread: (unread: boolean) => void;
  setCanLoadMore: (canLoadMore: boolean) => void;
  loadConversations: () => void;
  unlockChatbarList: () => void;
  clearUserTyping: () => void;
  getTicketData: () => Promise<any>;
  clearUserTypingTimeout: () => void;
  setUserTyping: (
    user: any,
    source?: "local" | "server",
    agent?: { agentName?: string; agentAvatarUrl?: string },
  ) => void;
  loadCurrentTicket: (shareToken: string) => void;
  clearCurrentTicket: () => void;
  // Focus-driven reopen of the single cached chatbar conversation. Resolves true
  // if a live conversation was opened, false if there was nothing to reopen or it
  // had been resolved (the cache is cleared in that case).
  reopenCachedChatbarTicket: () => Promise<boolean>;
  getConversations: (skip: number, limit: number) => Promise<any>;
  setTicketFilter: (filter: Record<string, any>) => void;
  sendComment: (
    message: string,
    files: Array<File | { url: string; name: string; type: string }>,
  ) => void;
  // Quoted-reply target for the composer (WhatsApp-style reply).
  replyingTo?: TicketChatMessage;
  setReplyingTo: (message?: TicketChatMessage) => void;
  loadComments: (silent?: boolean, reconcileBotTurn?: boolean) => Promise<any>;
  // Drops any live "Thinking…"/streaming bubble — a voice call ending mid-turn
  // leaves no event on the wire that would clear it.
  clearBotWrittingIndicator: () => void;
  rateComment: (commentId: string, rating: "positive" | "negative") => void;
  silentRefreshTickets: () => void;
  loadComment: (
    commentId: string,
    parentId: string,
    extraState?: Record<string, any>,
  ) => Promise<void>;
  commitPendingFinalComment: () => void;
  getCurrentTime: () => Date;
  pushComment: (comment: TicketChatMessage) => void;
  uploadFile: (
    file: File,
    onProgress?: (pct: number) => void,
    // Capture uploads pass the 100 MB capture limit; everything else keeps
    // the composer's 25 MB default.
    options?: { maxBytes?: number },
  ) => Promise<string>;
  uploadAudioRecording: (
    file: File,
    onProgress?: (pct: number) => void,
  ) => Promise<{ url: string; name: string; type: string }>;
  setUploadProgress: (index: number, pct: number) => void;
  isTyping: (typing: boolean) => void;
  setCurrentComments: (
    comments: TicketChatMessage[],
    extraState?: Record<string, any>,
  ) => void;
  // Marks one of Kai's cards answered right away (the server confirms with
  // comment.updated).
  // Returns the response it wrote, so a failed send can take exactly that
  // one back (restoreChatWidgetResponse).
  markChatWidgetAnswered: (
    commentId: string,
    response: {
      action: string;
      values: Record<string, string | string[]>;
      label: string;
      pending?: boolean;
    },
  ) => any;
  // The confirm card's Undo: stamps `undoneAt` on its response.
  markChatWidgetUndone: (commentId: string) => any;
  // Puts a card's response back to `previous` (undefined = unanswered), but
  // only while it still holds `written`: a server update that landed in
  // between wins.
  restoreChatWidgetResponse: (
    commentId: string,
    written: any,
    previous: any,
  ) => void;
  createNewConversation: (data: any, aiQuestion?: string) => Promise<any>;
  // Resolves (never rejects) with whether the answer reached the server. A
  // failure is fully carried by the errored optimistic bubble, so callers
  // don't need to handle it — none of them await this today.
  answerBotAction: (
    data: any,
    files: Array<File | { url: string; name: string; type: string }>,
  ) => Promise<AnswerBotActionResult>;
  handleWebSocketEvent: (webSocketEventData: WebSocketEventData) => void;
  updateCurrentTicket: (ticketId: string, data: any) => void;
  hasAnswerBotReply: () => boolean;
  loadTicket: (shareToken: string) => Promise<any>;
  refreshCurrentTicket: () => void;
  getBotResponseCount: () => number;
  lastMessageHasContextAnswer: () => boolean;
  lastMessageIsAIAnswer: () => boolean;
  getUnreadTickets: () => void;
}

export const useTicketStore = create<TicketState>()((set, get) => ({
  previousTickets: [],
  previousTicketsLoaded: false,
  chatbarListUnlocked: false,
  ticketFilter: {
    status: undefined,
  },
  currentTicket: undefined,
  loadingComments: false,
  userTypingTimeout: undefined,
  typingUser: undefined,
  typingSource: undefined,
  typingAgent: undefined,
  currentComments: [],
  uploadingAttachment: false,
  uploadProgress: [],
  pendingFinalComment: undefined,
  setUploadProgress: (index: number, pct: number) => {
    const next = [...get().uploadProgress];
    next[index] = pct;
    set({ uploadProgress: next });
  },
  conversationError: undefined,
  currentComposerText: undefined,
  unreadTickets: [],
  pendingAiQuestionText: undefined,
  pendingAiQuestionCreatedAt: undefined,
  pendingConversationIsBot: false,
  aiTakeoffActive: false,
  pendingComposerMessage: undefined,
  hasAnswerBotReply: () => {
    return get().currentComments.some((comment) => comment.data?.answerBot);
  },
  markChatWidgetAnswered: (commentId, response) => {
    const written = { ...response, at: new Date().toISOString() };
    get().setCurrentComments(
      get().currentComments.map((comment) =>
        comment.id === commentId && comment.data?.widget
          ? {
              ...comment,
              data: {
                ...comment.data,
                widget: {
                  ...comment.data.widget,
                  response: written,
                },
              },
            }
          : comment,
      ),
    );
    return written;
  },
  markChatWidgetUndone: (commentId) => {
    let written: any;
    get().setCurrentComments(
      get().currentComments.map((comment) => {
        if (comment.id !== commentId || !comment.data?.widget?.response) {
          return comment;
        }
        written = {
          ...comment.data.widget.response,
          undoneAt: new Date().toISOString(),
        };
        return {
          ...comment,
          data: {
            ...comment.data,
            widget: { ...comment.data.widget, response: written },
          },
        };
      }),
    );
    return written;
  },
  restoreChatWidgetResponse: (commentId, written, previous) => {
    if (!written) return;
    get().setCurrentComments(
      get().currentComments.map((comment) => {
        if (
          comment.id !== commentId ||
          comment.data?.widget?.response !== written
        ) {
          return comment;
        }
        const { response: _dropped, ...widget } = comment.data.widget;
        return {
          ...comment,
          data: {
            ...comment.data,
            widget: previous ? { ...widget, response: previous } : widget,
          },
        };
      }),
    );
  },
  setCurrentComments: (
    comments: TicketChatMessage[],
    extraState?: Record<string, any>,
  ) => {
    const commentsNew = comments ?? [];

    commentsNew.sort((a, b) => {
      const dateA = new Date(a.createdAt ?? new Date().toISOString());
      const dateB = new Date(b.createdAt ?? new Date().toISOString());
      return dateA.getTime() - dateB.getTime();
    });

    // The awaiting step is read off the last comment — ignoring the
    // diagnostic-logs notice, a system line the server appends whenever logs
    // arrive, which may land after a parked step and must not hide its input.
    const actionableComments = commentsNew.filter(
      (comment) => !isCaptureLogsNotice(comment),
    );

    let botAction = null;
    if (actionableComments.length > 0) {
      const lastComment = actionableComments[actionableComments.length - 1];
      if (lastComment.type === "BOT" && lastComment.data?.action) {
        botAction = {
          action: {
            ...lastComment.data?.action,
            createdAt: lastComment.createdAt,
          },
        };
      }
    }

    // Preserve passtoagent bot action when agent response doesn't include action data
    if (!botAction) {
      const existing = get().currentBotAction;
      if (existing?.action?.type === "passtoagent") {
        botAction = existing;
      }
    }

    if (
      botAction?.action?.type === "answerbotflow" &&
      actionableComments.length > 1
    ) {
      const previousComment =
        actionableComments[actionableComments.length - 2];
      if (
        previousComment &&
        previousComment.data?.collectEmail &&
        previousComment.data?.content?.length > 0
      ) {
        botAction = {
          action: {
            type: "input",
            inputType: "email",
            actionType: "email",
            aiCollect: true,
            title: previousComment.data?.content?.replace(/\*\*/g, ""),
            placeholder: useConfigStore.getState().translateText("yourEmail"),
          },
        };
      }
    }

    set({
      currentBotAction: botAction as any,
      currentComments: commentsNew,
      ...(extraState ?? {}),
    });
  },
  setCurrentComposerText: (text: string) => {
    set({
      currentComposerText: text,
    });
  },
  clearCurrentTicket: () => {
    clearBotWrittingWatchdog();
    cancelMissingActionRefetch();
    set({
      currentTicket: undefined,
      canSetBotWrittingText: false,
      botWrittingText: undefined,
      botWrittingIsThinking: false, botWrittingState: undefined,
      pendingFinalComment: undefined,
      pendingAiQuestionText: undefined,
      pendingAiQuestionCreatedAt: undefined,
      pendingComposerMessage: undefined,
      replyingTo: undefined,
    });
    get().setCurrentComments([]);
    get().clearUserTyping();
  },
  replyingTo: undefined,
  setReplyingTo: (message?: TicketChatMessage) => {
    set({ replyingTo: message });
  },
  setTicketUnread: (unread = false) => {
    const currentTicket = get().currentTicket;

    if (currentTicket) {
      set({
        currentTicket: {
          ...currentTicket,
          sessionNotificationsUnread: unread,
        },
      });

      if (!unread) {
        set({
          unreadTickets: get().unreadTickets.filter(
            (ticket) => ticket.id !== currentTicket.id,
          ),
        });
      }

      get().setCurrentComments(
        get().currentComments.map((comment) => {
          if (comment?.id === currentTicket?.id) {
            return {
              ...comment,
              sessionNotificationsUnread: unread,
            };
          }
          return comment;
        }),
      );
      set({
        previousTickets: get().previousTickets.map((ticket) => {
          if (ticket?.id === currentTicket?.id) {
            return {
              ...ticket,
              sessionNotificationsUnread: unread,
            };
          }
          return ticket;
        }),
      });
    }
  },
  setCanLoadMore: (canLoadMore: boolean) => {
    set({
      canLoadMore,
    });
  },
  updateCurrentTicket: (ticketId, data) => {
    if (get().currentTicket?.id === ticketId) {
      set((state) => ({
        currentTicket: {
          ...state.currentTicket,
          ...data,
        },
      }));
    }

    // New array — an in-place patch keeps the same reference, so Zustand
    // subscribers (conversation cards) would not re-render the change.
    set({
      previousTickets: get().previousTickets.map((ticket: Ticket) =>
        ticket.id === ticketId ? { ...ticket, ...data } : ticket,
      ),
    });
  },
  getTicketData: () => {
    // On the web this round trip stays in the same JS context and answers in a
    // few ms. Native SDKs collect the data on-device and are far slower — iOS
    // reads console logs out of OSLogStore, which routinely takes longer than a
    // second on a real device. The reply is all-or-nothing: when it times out the
    // conversation is created with NO environment data, custom data or logs at
    // all, and nothing ever backfills it. So app mode gets a much larger budget —
    // the customer is on the conversation loading screen either way.
    const timeoutMs = useConfigStore.getState().isApp
      ? COLLECT_TICKET_DATA_TIMEOUT_APP
      : COLLECT_TICKET_DATA_TIMEOUT_WEB;

    return new Promise((resolve) => {
      CommunicationManager.getInstance()
        .sendMessageWithResolver({ name: "collect-ticket-data" }, timeoutMs)
        .then((data) => {
          resolve(data);
        })
        .catch(() => {
          resolve({});
        });
    });
  },
  setTicketFilter: (filter) => {
    set({
      ticketFilter: filter,
    });

    get().loadConversations();
  },
  createNewConversation: async (data, aiQuestion?: string) => {
    try {
      // The question the user typed into the home AI chatbar (if any). It's
      // sent to the server with the create request; the server runs the
      // workflow and then delivers this question into the conversation —
      // answered by AI/agent when the workflow has an AI step, or recorded as a
      // comment after the workflow runs through when it doesn't. (Previously the
      // client stashed and re-sent it, which silently dropped it for workflows
      // without an AI step, e.g. pass-to-agent or human routing.)
      const aiQuestionText =
        typeof aiQuestion === "string" && aiQuestion.trim().length > 0
          ? aiQuestion
          : undefined;

      // The home composer can carry an attachment (or audio) with the first
      // message. When it does we do NOT seed the question server-side (that
      // would create a text-only comment that duplicates our message); instead
      // we stash it and let the conversation view deliver text + attachment as
      // one message via the normal send path (so the AI sees the attachment).
      const firstMessageAttachments = Array.isArray(
        data.firstMessageAttachments,
      )
        ? data.firstMessageAttachments
        : [];
      const hasFirstMessageAttachments = firstMessageAttachments.length > 0;

      // Mirror the question for the takeoff / loading shell / conversation
      // handover. Clearing currentBotAction here fixes a stale-action leak: a
      // passtoagent action from a previous conversation survives
      // clearCurrentTicket (it is deliberately preserved there) and would
      // briefly render on the new conversation until the fresh action lands.
      set({
        pendingAiQuestionText: aiQuestionText,
        pendingAiQuestionCreatedAt: aiQuestionText ? new Date() : undefined,
        // A workflow id, or a project whose workflows pick new conversations
        // up. A direct agent conversation (startAgent) fronts the agent.
        pendingConversationIsBot:
          !data.agentId &&
          (!!data.botId ||
            useConfigStore.getState().config?.conversationStartsWithBot ===
              true),
        currentBotAction: undefined,
        // Drop any error from a previous attempt, otherwise the loading shell
        // opens straight into the stale failure instead of the new takeoff.
        conversationError: undefined,
        pendingComposerMessage: hasFirstMessageAttachments
          ? {
              text: aiQuestionText ?? defaultAttachmentMessage,
              attachments: firstMessageAttachments,
            }
          : undefined,
      });

      // While the home composer is animating down into the conversation
      // position (navDelayMs > 0), navigation waits for the choreography;
      // everything else (ticket data collection, creation) runs in parallel.
      const navDelayMs =
        typeof data.navDelayMs === "number" && data.navDelayMs > 0
          ? data.navDelayMs
          : 0;

      // The customer just asked a question and is expecting a reply. Mirror
      // answerBotAction and allow the "Thinking…" / streaming indicator to
      // render: the question is delivered + answered server-side, so the client
      // never goes through answerBotAction (which normally sets this), and the
      // BOT_WRITTING events would otherwise be gated out by canSetBotWrittingText.
      // Harmless when no AI answers — no BOT_WRITTING arrives, so nothing shows.
      if (aiQuestionText) {
        set({ canSetBotWrittingText: true });
      }

      // Notify flow started.
      Communicator.notifyEvent("conversation-started", {
        workflowId: data.botId ?? "",
      });

      // Track manual conversation starts (home "start conversation" / "ask a
      // question") for analytics. Auto-started flows are not counted.
      if (data.isManualStart || data.aiQuestion) {
        Communicator.trackWidgetEvent("gleap-conversation-started", {
          botId: data.botId ?? "",
          aiQuestion: data.aiQuestion ?? false,
          isManualStart: data.isManualStart ?? false,
        });
      }

      // Show the loading page immediately so the user sees a response to their
      // Send tap. Native SDKs (iOS OSLogStore in particular) can take several
      // hundred ms to collect ticket data; keeping the composer visible during
      // that window feels broken.
      //
      // With a takeoff, ticket-data collection starts mid-flight (its
      // main-thread cost overlaps the animation instead of stalling it) and
      // navigation is held until the composer has docked.
      const ticketDataPromise =
        navDelayMs > 0
          ? new Promise((resolve) => setTimeout(resolve, 300)).then(() =>
              get().getTicketData(),
            )
          : undefined;

      if (navDelayMs > 0) {
        await new Promise((resolve) => setTimeout(resolve, navDelayMs));
      }

      useRouterStore.getState().pushPage("conversationloading", {});
      set({ aiTakeoffActive: false });

      const ticketData = await (ticketDataPromise ?? get().getTicketData());

      const source =
        useRouterStore.getState().appMode === AppMode.CHATBAR
          ? "chatbar"
          : typeof data.source === "string"
            ? data.source
            : undefined;

      const ticketService = HttpTicketService.getInstance();
      const createdTicket = await ticketService.createConversation({
        botId: data.botId ?? "",
        aiQuestion: data.aiQuestion ?? false,
        isManualStart: data.isManualStart ?? false,
        // Agentic chatbar question — delivered server-side once the workflow
        // settles. `clientTools` is forwarded so the AI keeps tool access on
        // this first answer (it can't read the client's tools server-side).
        // Skipped when the first message carries an attachment: it is
        // delivered client-side instead (see pendingComposerMessage), so
        // seeding it here would duplicate the customer's message.
        ...(aiQuestionText && !hasFirstMessageAttachments
          ? {
              aiQuestionText,
              clientTools: useConfigStore.getState().agentTools,
            }
          : {}),
        // Gleap.startAgent(): start a direct conversation with an AI agent
        // (not a workflow/bot). The server engages the agent without a
        // workflow — see createSharedTicket. Sent INSTEAD of botId.
        ...(data.agentId ? { agentId: data.agentId } : {}),
        // Gleap.startAgent({ initialMessage }): a static greeting FROM the
        // agent, posted as the conversation's first bot message server-side.
        ...(data.initialMessage
          ? { initialMessage: data.initialMessage }
          : {}),
        // Optional agent context from Gleap.startAgent({ context }). Forwarded
        // for the server to attach to the conversation when supported.
        ...(data.context ? { context: data.context } : {}),
        ...(source ? { source } : {}),
        // A "Pass to agent" home item: the server gives Kai the team's
        // instructions it names by key (the widget never sees their text).
        ...(typeof data.kaiInstructionsKey === "string" &&
        data.kaiInstructionsKey
          ? { kaiInstructionsKey: data.kaiInstructionsKey }
          : {}),
        ...ticketData,
      });

      // A workflow on the new conversation may have already redirected the
      // customer to an existing ticket (BOT_OPENTICKET). If so, don't navigate
      // back to the new (now archived) conversation.
      const wasRedirected = Date.now() - redirectedToShareTokenAt < 5000;

      // Remember the just-created chatbar conversation as the single "reopen on
      // focus" slot. Skip if a workflow redirected us elsewhere (that ticket is
      // archived). Guarded to chatbar mode so widget behaviour is unchanged.
      if (
        !wasRedirected &&
        useRouterStore.getState().appMode === AppMode.CHATBAR
      ) {
        saveActiveChatbarTicket(
          useSessionStore.getState().session?.gleapId,
          createdTicket,
        );
      }

      if (wasRedirected) {
        // Stay on the conversation the workflow switched us to. The mirrored
        // question belongs to the (now archived) new conversation — drop it so
        // it can't render as a pending bubble on the redirect target.
        set({
          pendingAiQuestionText: undefined,
          pendingAiQuestionCreatedAt: undefined,
        });
      } else if (createdTicket && createdTicket.shareToken) {
        // Open conversations.
        useRouterStore.getState().pushPage("conversation", {
          shareToken: createdTicket.shareToken,
        });
      } else {
        // Close widget.
        Communicator.closeWidget();
      }
    } catch (err: any) {
      // Creation failed — reset the takeoff state so the home composer's
      // failure-recovery effect can restore itself (and the loading shell
      // falls back to its plain state).
      //
      // conversationError is what takes the customer OFF the endless spinner:
      // we are already on the "conversationloading" page here, and nothing else
      // navigates away, so without it the loading shell spins forever (reported
      // as "the chat is stuck on the loading screen"). ConversationLoading
      // renders it as an error with a button back to where they came from.
      set({
        pendingAiQuestionText: undefined,
        pendingAiQuestionCreatedAt: undefined,
        pendingComposerMessage: undefined,
        aiTakeoffActive: false,
        pendingConversationIsBot: false,
        conversationError: useConfigStore
          .getState()
          .translateTextWithFallback(
            "conversationCreateFailed",
            "We couldn't start this conversation. Please try again.",
          ),
      });

      // Gleap.startAgent() with an agent that lacks the "Reply to customer"
      // tool: the server rejects creation (the agent could never answer). Log it
      // so developers see why the conversation didn't start.
      if (err?.data?.code === "AGENT_NO_REPLY_TOOL") {
        console.error("Agent does not have reply tool!");
      }
    }
  },
  answerBotAction: async (
    data: any,
    attachments: Array<File | { url: string; name: string; type: string }>,
  ) => {
    return new Promise(async (resolve, reject) => {
      const sessionStore = useSessionStore.getState();
      const shareToken = get().currentTicket?.shareToken;

      if (!shareToken || shareToken.length === 0) {
        reject(new Error("Failed"));
        return;
      }

      const previousBotAction = get().currentBotAction;
      let action = previousBotAction?.action;

      // Clear immediately so the button group unmounts and a rapid
      // second click cannot fire another submission for the same action.
      if (previousBotAction) {
        set({ currentBotAction: undefined });
      }

      // ── Optimistic insert (Claude-Code / iMessage style) ──
      //
      // A `question` payload only ever comes from an agent-bound text
      // send (composer / auto-send), so push the user's bubble into the
      // timeline BEFORE resolving the action, the attachment upload and
      // the HTTP POST — it must show up the instant they hit send. We
      // patch it post-response with the authoritative server time +
      // final attachments — mirrors the already-working pattern in
      // `sendComment`.
      //
      // This deliberately doesn't gate on the action type: the composer
      // also sends questions when `currentBotAction` is empty (the
      // fallback below then scans old comments and even sleeps 2s), and
      // the user's bubble must not wait for any of that.
      //
      // Other action types (button click, calendly, ratings) and the
      // post-response BOT-question echo continue to flow through the
      // existing branches below — buttons land instantly via their own
      // UI feedback, and the BOT echo represents the bot's words, not
      // the user's, so post-response timing for it is fine.
      const userTextForOptimistic =
        typeof data?.question === "string" && data.question.trim().length > 0
          ? data.question
          : null;
      const isUserTextSend =
        !(data.action && data.action.length > 0) &&
        userTextForOptimistic !== null;
      const optimisticMessageId = isUserTextSend ? getRandomMessageId() : null;
      if (isUserTextSend && optimisticMessageId) {
        const localTime = get().getCurrentTime();
        // Seed the bubble with any attachments that are ALREADY uploaded
        // (descriptors carrying a url — e.g. a voice note the recorder uploaded
        // before send). Without this the bubble renders with only the
        // "📎 Attachment" placeholder text until the upload patch lands below —
        // briefly flashing the label instead of the voice player, most visibly
        // on the first message from the home takeoff where the action-fallback
        // sleep widens that window. File uploads (no url yet) still patch in
        // after they resolve.
        const optimisticAttachments = attachments
          .filter(
            (entry): entry is { url: string; name: string; type: string } =>
              !(entry instanceof File) && !!entry?.url,
          )
          .map((entry) => ({
            url: entry.url,
            name: entry.name,
            type: entry.type,
          }));
        get().pushComment({
          id: optimisticMessageId,
          updatedAt: localTime,
          createdAt: localTime,
          session: sessionStore.session!,
          type: "BOT_REPLY",
          // Same escaped form the server stores, so the bubble renders the
          // same before and after a refresh (ChatMessage decodes it).
          data: {
            content: sanitizeHtmlHelper(userTextForOptimistic!),
            type: "text",
          },
          // Already-uploaded attachments show immediately; File uploads are
          // patched in after they complete.
          attachments: optimisticAttachments,
          sending: true,
        });
      }

      // Action fallback to latest comment.
      if (!action) {
        action = get()
          .currentComments.slice()
          .reverse()
          .find((comment) => comment.type === "BOT" && comment.data?.action)
          ?.data?.action;

        if (action) {
          // Wait for one second to make sure the action is set.
          await new Promise((resolve) => setTimeout(resolve, 2000));
        }
      }

      let attachmentsUrls: any[] = [];
      try {
        set({ uploadProgress: attachments.map(() => 0) });
        for (let i = 0; i < attachments.length; i++) {
          const entry = attachments[i];
          if (entry instanceof File) {
            try {
              const fileUrl = await get().uploadFile(entry, (pct) => {
                get().setUploadProgress(i, pct);
              });
              attachmentsUrls.push({
                url: fileUrl,
                name: entry.name,
                type: entry.type,
              });
            } catch (exp) {
              console.error("Attachment upload failed:", exp);
            }
          } else {
            attachmentsUrls.push({
              url: entry.url,
              name: entry.name,
              type: entry.type,
            });
          }
        }
      } catch (exp) {
        console.error("Error uploading attachments:", exp);
      } finally {
        set({ uploadProgress: [] });
      }

      // Clear current bot action
      set({
        canSetBotWrittingText: true,
      });

      const finalAttachments =
        attachmentsUrls.length > 0
          ? attachmentsUrls
          : data?.attachment
            ? [data?.attachment]
            : [];

      // Get the server time because of time issues on Windows. Throws on any
      // HTTP failure — the catch below turns that into an errored bubble
      // instead of the fabricated success that made the message "disappear"
      // on the next refetch (#146330).
      const answerClientMessageId = newClientMessageId();
      const sendAnswerAndGetTime = async (): Promise<Date> => {
        const httpClient = new HttpClientXHR();
        const answerAppMode = useRouterStore.getState().appMode;
        const answerSource =
          answerAppMode === AppMode.CHATBAR
            ? "chatbar"
            : answerAppMode === AppMode.WIDGET
              ? "widget"
              : undefined;
        const response = await httpClient.post(
          `/v3/shared/tickets/${shareToken}/workflow/answer`,
          {
            // Always carry the typed answer as `text` too: the server resolves
            // the awaiting step from the ticket, and a chatinput step reads
            // `text` — a stale local action must not turn the answer into ""
            // (#144422).
            ...withCollectedAnswerText(data),
            clientMessageId: answerClientMessageId,
            attachments: finalAttachments,
            ...(answerSource ? { source: answerSource } : {}),
          },
          { timeout: ANSWER_WORKFLOW_TIMEOUT_MS, retry: false },
        );

        // Answering into a chatbar conversation makes it the latest one to
        // reopen on focus. Only reached when the POST above succeeded.
        if (answerAppMode === AppMode.CHATBAR) {
          saveActiveChatbarTicket(
            useSessionStore.getState().session?.gleapId,
            get().currentTicket,
          );
        }

        if (response.status === 204) {
          const serverTime = response.headers["date"];
          if (serverTime) {
            return new Date(serverTime);
          }
        }

        return get().getCurrentTime();
      };

      let currentTime: Date;
      try {
        currentTime = await sendAnswerAndGetTime();
      } catch (error) {
        const reason = classifyAnswerSendError(error);
        // A 409 means the conversation can no longer take replies — say so on
        // the bubble and pull the ticket so the ended state renders.
        const errorMessage =
          reason === "closed"
            ? useConfigStore.getState().translateText("conversationEnded")
            : undefined;

        if (optimisticMessageId) {
          get().setCurrentComments(
            applyAnswerSendOutcome(get().currentComments, {
              optimisticId: optimisticMessageId,
              success: false,
              errorMessage,
            }),
          );
        } else if (
          !(data.action && data.action.length > 0) &&
          typeof data?.message === "string" &&
          data.message.trim().length > 0
        ) {
          // Text send without an optimistic bubble (chatinput / input steps
          // push theirs post-response) — the failure still needs a bubble.
          get().pushComment({
            id: getRandomMessageId(),
            updatedAt: get().getCurrentTime(),
            createdAt: get().getCurrentTime(),
            session: sessionStore.session!,
            type: "BOT_REPLY",
            data: { content: data.message, type: "text" },
            attachments: finalAttachments,
            sending: false,
            error: true,
            errorMessage,
          });
        }

        // Nothing was submitted: bring the step's buttons / input back so the
        // customer can retry, and keep the "Thinking…" indicator gated off.
        set({
          canSetBotWrittingText: false,
          ...(previousBotAction ? { currentBotAction: previousBotAction } : {}),
        });

        if (reason === "closed") {
          get().refreshCurrentTicket();
        }

        resolve({ sent: false, reason });
        return;
      }

      if (
        action?.type === "answerbotflow" ||
        action?.type === "aitask" ||
        action?.type === "passtoagent"
      ) {
        if (!shouldMuteWidgetSounds()) {
          Communicator.playPing();
        }

        if (data.action && data.action.length > 0) {
          get().pushComment({
            id: getRandomMessageId(),
            updatedAt: currentTime,
            createdAt: currentTime,
            session: sessionStore.session!,
            type: "BOT_REPLY",
            data: {
              content: data?.message,
              type: "text",
            },
            attachments: finalAttachments,
            sending: false,
          });
        } else {
          // We intentionally do NOT echo the answerbotflow greeting
          // (`action.question`) into the timeline here. The greeting is a real,
          // server-stored BOT/answerbotflow comment that ConversationMessageList
          // now renders as soon as it stops being the unanswered tail (i.e. the
          // moment the customer replies). Pushing a second, client-only copy
          // rendered the greeting twice — the duplicate that disappeared on
          // reload because it never existed on the server.

          if (optimisticMessageId) {
            // Patch the optimistic bubble we pushed at the top of this
            // call: clear `sending`, snap timestamps to the server's
            // clock, attach any uploaded files. No new pushComment —
            // the comment is already in the timeline.
            get().setCurrentComments(
              applyAnswerSendOutcome(get().currentComments, {
                optimisticId: optimisticMessageId,
                success: true,
                serverTime: currentTime,
                attachments: finalAttachments,
              }),
            );
          } else {
            // No optimistic insert (no text payload) — fall back to the
            // original post-response push.
            get().pushComment({
              id: getRandomMessageId(),
              updatedAt: currentTime,
              createdAt: currentTime,
              session: sessionStore.session!,
              type: "BOT_REPLY",
              data: {
                content: data.question,
                type: "text",
              },
              attachments: finalAttachments,
              sending: false,
            });
          }

          const answeredCard = data?.widgetResponse
            ? get().currentComments.find(
                (comment) => comment.id === data.widgetResponse.commentId,
              )?.data?.widget
            : undefined;
          if (
            !isSettledWidgetAnswer(
              answeredCard?.type,
              data?.widgetResponse?.action,
            )
          ) {
            get().setUserTyping(
              {
                id: "bot",
                name: "Bot",
                avatar: "",
              },
              "local",
            );
          }
        }
      } else if (action?.type === "calendly") {
        // Do nothing.
      } else {
        if (!shouldMuteWidgetSounds()) {
          Communicator.playPing();
        }

        if (optimisticMessageId) {
          // The user's bubble was already pushed optimistically (question
          // text send whose action resolved to none/other) — patch it
          // with the server time + uploaded attachments instead of
          // pushing the same text a second time.
          get().setCurrentComments(
            applyAnswerSendOutcome(get().currentComments, {
              optimisticId: optimisticMessageId,
              success: true,
              serverTime: currentTime,
              attachments: finalAttachments,
            }),
          );
        } else {
          // Push local message.
          get().pushComment({
            id: getRandomMessageId(),
            updatedAt: currentTime,
            createdAt: currentTime,
            session: sessionStore.session!,
            type:
              (action?.type === "input" ||
                action?.type === "rateconversation") &&
              !action?.aiCollect
                ? "BOT_INPUT_REPLY"
                : "BOT_REPLY",
            data: {
              content: data.message,
              rating: data.rating,
              type: action?.type ?? "text",
              question: action?.title ?? action?.placeholder ?? "",
            },
            attachments: finalAttachments,
            sending: true,
          });
        }
      }

      // Update bot with new action. Errored bubbles from an earlier failed
      // send keep their state — only this send's in-flight flags are cleared.
      const updatedComments = get().currentComments.map((comment) => {
        if (comment.sending && !comment.error) {
          comment.sending = false;
        }
        return comment;
      });

      get().setCurrentComments(updatedComments);

      // A button click / collected answer advances the workflow to its next
      // step; if that step's comment never arrives, re-fetch for it.
      armMissingActionRefetch(get);

      resolve({ sent: true });
    });
  },
  uploadFile(file, onProgress, options) {
    return new Promise((resolve, reject) => {
      const maxBytes = options?.maxBytes ?? CONVERSATION_ATTACHMENT_LIMIT_BYTES;
      if (file.size > maxBytes) {
        reject(new Error(CONVERSATION_ATTACHMENT_SIZE_ERROR_MESSAGE));
        return;
      }

      const sessionStore = useSessionStore.getState();

      var formData = new FormData();
      formData.append("file", file);

      var xhr = new XMLHttpRequest();
      xhr.open("POST", sessionStore.apiUrl + "/uploads/attachments");
      if (sessionStore.session) {
        xhr.setRequestHeader("Api-Token", sessionStore.sdkKey);
        xhr.setRequestHeader("Gleap-Id", sessionStore.session.gleapId ?? "");
        xhr.setRequestHeader(
          "Gleap-Hash",
          sessionStore.session.gleapHash ?? "",
        );
      }
      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
      }
      xhr.onerror = function () {
        reject(new Error("Upload failed"));
      };
      xhr.onreadystatechange = function () {
        if (xhr.readyState !== 4) return;
        if (xhr.status === 200 && xhr.responseText) {
          try {
            const data = JSON.parse(xhr.responseText);
            if (data?.fileUrls?.length > 0) {
              resolve(data.fileUrls[0]);
              return;
            }
          } catch (_) {}
        }
        if (xhr.status === 413) {
          reject(new Error(CONVERSATION_ATTACHMENT_SIZE_ERROR_MESSAGE));
          return;
        }
        reject(new Error("Upload failed"));
      };

      xhr.send(formData);
    });
  },
  uploadAudioRecording(file, onProgress) {
    return new Promise((resolve, reject) => {
      if (file.size > CONVERSATION_ATTACHMENT_LIMIT_BYTES) {
        reject(new Error(CONVERSATION_ATTACHMENT_SIZE_ERROR_MESSAGE));
        return;
      }

      const sessionStore = useSessionStore.getState();

      var formData = new FormData();
      formData.append("file", file);

      var xhr = new XMLHttpRequest();
      // Dedicated endpoint: the server transcodes the raw recording to MP3 so
      // it plays in every browser, then returns the stored MP3 descriptor.
      xhr.open("POST", sessionStore.apiUrl + "/uploads/audio");
      if (sessionStore.session) {
        xhr.setRequestHeader("Api-Token", sessionStore.sdkKey);
        xhr.setRequestHeader("Gleap-Id", sessionStore.session.gleapId ?? "");
        xhr.setRequestHeader(
          "Gleap-Hash",
          sessionStore.session.gleapHash ?? "",
        );
      }
      if (onProgress) {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            onProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
      }
      xhr.onerror = function () {
        reject(new Error("Upload failed"));
      };
      xhr.onreadystatechange = function () {
        if (xhr.readyState !== 4) return;
        if (xhr.status === 200 && xhr.responseText) {
          try {
            const data = JSON.parse(xhr.responseText);
            if (data?.fileUrl) {
              resolve({
                url: data.fileUrl,
                name: data.name ?? "recorded-audio.mp3",
                type: data.type ?? "audio/mpeg",
              });
              return;
            }
          } catch (_) {}
        }
        if (xhr.status === 413) {
          reject(new Error(CONVERSATION_ATTACHMENT_SIZE_ERROR_MESSAGE));
          return;
        }
        reject(new Error("Upload failed"));
      };

      xhr.send(formData);
    });
  },
  getCurrentTime: () => {
    try {
      const comments = get().currentComments;
      const lastComment = comments[comments.length - 1];
      if (!lastComment) {
        return new Date();
      }

      const lastCommentDate = moment.utc(lastComment.createdAt).toDate();

      if (
        lastComment?.createdAt &&
        moment.utc(lastCommentDate).isBefore(moment.utc())
      ) {
        return moment.utc().add(1, "milliseconds").toDate();
      }
      const date = new Date(lastComment.createdAt ?? new Date().toISOString());
      date.setUTCMilliseconds(date.getUTCMilliseconds() + 1);
      return date;
    } catch {
      return new Date();
    }
  },
  loadTicket: async (shareToken: string) => {
    try {
      const ticket = await HttpTicketService.getInstance().findOne({
        id: shareToken,
      });

      return ticket;
    } catch (_) {}
  },
  refreshCurrentTicket: () => {
    const oldTicketShareToken = get().currentTicket?.shareToken;
    if (oldTicketShareToken) {
      get()
        .loadTicket(oldTicketShareToken)
        .then((data) => {
          // loadTicket resolves undefined on failure — keep the ticket we
          // already have instead of wiping the live conversation (which
          // hides the composer and strands a "Typing…" indicator). This
          // runs on every widget close→open, exactly when the network may
          // still be waking up.
          if (data) {
            set({
              currentTicket: data,
            });
          }
        });
    }
  },
  loadCurrentTicket: (shareToken: string) => {
    const oldTicketShareToken = get().currentTicket?.shareToken;
    if (oldTicketShareToken !== shareToken) {
      get()
        .loadTicket(shareToken)
        .then((data) => {
          try {
            get().clearUserTyping();
            set({ pendingConversationIsBot: false });

            // Fetch failed (loadTicket swallows errors and resolves
            // undefined). Entering the loading state anyway used to strand
            // the spinner forever: loadComments() rejects on the missing
            // ticket BEFORE its loadingComments:false, and the 45s reload
            // fallback is gated on currentTicket — permanently a no-op. Keep
            // loadingComments false so the retry path can run cleanly.
            if (!data) {
              set({
                currentTicket: undefined,
                loadingComments: false,
              });
              get().setCurrentComments([]);
              return;
            }

            set({
              currentTicket: data,
              loadingComments: true,
            });
            get().setCurrentComments([]);

            // Load comments.
            get()
              .loadComments()
              .then(() => {
                armMissingActionRefetch(get);
              });
          } catch (exp) {}
        });
    }
  },
  reopenCachedChatbarTicket: async () => {
    const gleapId = useSessionStore.getState().session?.gleapId;
    const cached = getActiveChatbarTicket(gleapId);
    if (!cached) return false;

    // Already showing it (e.g. a rapid refocus) — nothing to fetch.
    if (get().currentTicket?.shareToken === cached.shareToken) return true;

    const ticket = await get().loadTicket(cached.shareToken);

    // Gone (fetch failed) or resolved (DONE/CLOSED) → forget it and fall back to
    // the blank bar. The fresh fetch is the source of truth.
    if (!ticket || !isReopenableStatus(ticket.status)) {
      clearActiveChatbarTicket(gleapId);
      get().clearCurrentTicket();
      return false;
    }

    get().clearUserTyping();
    set({ currentTicket: ticket, loadingComments: true });
    get().setCurrentComments([]);
    get().loadComments();
    return true;
  },
  clearBotWrittingIndicator: () => {
    set({
      botWrittingText: undefined,
      botWrittingIsThinking: false,
      canSetBotWrittingText: false,
    });
  },
  loadComments: async (silent = false, reconcileBotTurn = false) => {
    const { currentTicket } = get();
    if (!currentTicket) {
      return Promise.reject();
    }

    if (!silent) {
      set({
        loadingComments: true,
      });
    }

    try {
      const comments = await HttpMessageService.getInstance().find({
        query: {
          ticketShareToken: currentTicket.shareToken,
          vs: getVisibilityState(),
        },
      });

      // A refetch (e.g. tab refocus) can already contain the bot's final
      // reply while the streaming bubble is still up — showing both would
      // duplicate the message. Only treat the turn as over when the fetched
      // list contains the exact comment staged as `pendingFinalComment`:
      // that comment only exists once the bot's final MESSAGE_CREATED
      // arrived, so this can never match a reply that is still streaming
      // (a broader "unknown answerBot comment" check did, and it killed
      // live streams).
      const pendingId = get().pendingFinalComment?.id;
      const containsPendingFinalReply =
        !!pendingId &&
        (comments ?? []).some((c: TicketChatMessage) => c.id === pendingId);

      // A silent refetch can resolve MID-STREAM before the reply's own
      // MESSAGE_CREATED was applied (that handler's findOne round-trip is
      // still in flight, or the event is still on the wire): the fetched
      // snapshot then already contains the final AI reply while the streaming
      // bubble is still animating, and rendering both shows the message twice.
      // The missing-action refetch timers and the refocus/open refetches all
      // land in this window. Stage the not-yet-rendered reply for the
      // typewriter instead of appending it — the exact stash
      // `applyCreatedComment` uses — so the bubble finishes animating and the
      // saved comment lands exactly once on commit. Only the NEW tail of the
      // snapshot is considered (ids the client has never rendered): known
      // replies from earlier turns must keep appending untouched.
      const streamingWithoutPending =
        !pendingId &&
        !!get().botWrittingText &&
        (get().botWrittingText as string).length > 0;
      let fetchedComments: TicketChatMessage[] = comments ?? [];
      let stagedReply: TicketChatMessage | undefined;
      if (streamingWithoutPending) {
        const knownIds = new Set(
          get().currentComments.map((c: TicketChatMessage) => c.id),
        );
        for (let i = fetchedComments.length - 1; i >= 0; i--) {
          const candidate = fetchedComments[i];
          // First already-rendered comment = end of the new tail.
          if (knownIds.has(candidate.id)) break;
          if (
            candidate.type === "BOT" &&
            candidate.data?.answerBot === true
          ) {
            stagedReply = candidate;
            fetchedComments = fetchedComments.filter(
              (c: TicketChatMessage) => c.id !== candidate.id,
            );
            break;
          }
        }
      }

      // On the widget's closed→open refetch, events were being DROPPED right
      // up until now (MESSAGE_CREATED is gated on isWidgetOpen), so the
      // pendingFinalComment path above cannot have seen a turn that ended
      // while the widget was closed — the bubble-clearing comment never
      // applied, stranding "Thinking…" under the reply this very refetch just
      // brought in, with nothing left on the wire to clear it. Derive the
      // turn state from the snapshot instead (see snapshotEndsBotTurn):
      // turn over → drop the stale bubble; customer tail → allow the
      // indicator again so the still-running turn's events render. A staged
      // typewriter commit is left to its animation, as everywhere else.
      let reconciledState: Partial<TicketState> | undefined;
      if (reconcileBotTurn && !pendingId && !stagedReply) {
        const endsTurn = snapshotEndsBotTurn(comments, {
          voiceCallBusy: isVoiceCallBusy(),
        });
        if (endsTurn === true) {
          reconciledState = {
            canSetBotWrittingText: false,
            botWrittingText: undefined,
            botWrittingIsThinking: false, botWrittingState: undefined,
          };
        } else if (endsTurn === false) {
          reconciledState = { canSetBotWrittingText: true };
        }
      }

      // The snapshot is authoritative, but local bubbles that are still in
      // flight or failed to send exist nowhere else — carry them over instead
      // of letting the refocus refetch make the customer's message vanish.
      get().setCurrentComments(
        mergeRefetchedComments(get().currentComments, fetchedComments),
        containsPendingFinalReply
          ? {
              canSetBotWrittingText: false,
              botWrittingText: undefined,
              botWrittingIsThinking: false, botWrittingState: undefined,
              pendingFinalComment: undefined,
            }
          : stagedReply
            ? // Mirror of applyCreatedComment's mid-stream stash.
              { canSetBotWrittingText: false, pendingFinalComment: stagedReply }
            : reconciledState,
      );
    } catch (_) {}

    set({
      loadingComments: false,
    });
  },
  rateComment: async (commentId: string, rating: "positive" | "negative") => {
    const { currentTicket } = get();
    if (!currentTicket) {
      return;
    }

    // Locally update rating.
    get().setCurrentComments(
      get().currentComments.map((comment) => {
        if (comment.id === commentId) {
          comment.rating = rating;
        }
        return comment;
      }),
    );

    try {
      await HttpMessageService.getInstance().rateMessage({
        messageId: commentId,
        rating,
      });
    } catch (_) {}
  },
  isTyping: async (typing: boolean) => {
    const { currentTicket } = get();
    if (!currentTicket || !currentTicket.shareToken) {
      return;
    }

    try {
      await HttpTicketService.getInstance().indicateTyping({
        ticketShareToken: currentTicket.shareToken,
        typing,
      });
    } catch (_) {}
  },
  loadComment: async (
    commentId: string,
    parentId: string,
    extraState?: Record<string, any>,
  ) => {
    const currentTicket = get().currentTicket;
    if (!currentTicket) {
      return;
    }

    try {
      const fetchedComment = await HttpMessageService.getInstance().findOne({
        id: commentId,
        query: {
          ticketShareToken: currentTicket.shareToken,
        },
      });

      var newComments = dropSendingLocalsFor(
        get().currentComments,
        fetchedComment,
      );

      if (currentTicket?.id === parentId) {
        // Check if comment already exists.
        const index = newComments.findIndex(
          (comment) => comment.id === fetchedComment.id,
        );
        if (index === -1) {
          newComments.push(fetchedComment);
        }

        get().setCurrentComments(newComments, extraState);
        return;
      }
    } catch (_) {}

    if (extraState) {
      set(extraState as any);
    }
  },
  commitPendingFinalComment: () => {
    const pending = get().pendingFinalComment;
    if (!pending) return;

    const filtered = get().currentComments.filter((c) => !c.sending);
    const exists = filtered.some((c) => c.id === pending.id);
    const newComments = exists ? filtered : [...filtered, pending];

    // Atomic: new comment lands AND the streaming bubble clears in one render.
    get().setCurrentComments(newComments, {
      botWrittingText: undefined,
      botWrittingIsThinking: false, botWrittingState: undefined,
      pendingFinalComment: undefined,
    });
  },
  pushComment: (comment: TicketChatMessage) => {
    get().setCurrentComments([...get().currentComments, comment]);
  },
  sendComment: async (
    message: string,
    attachments: Array<File | { url: string; name: string; type: string }>,
  ) => {
    const { session } = useSessionStore.getState();
    const { currentTicket, replyingTo } = get();
    const fakeMessageId = getRandomMessageId();
    const clientMessageId = newClientMessageId();

    if (!currentTicket || !session) {
      return;
    }

    Communicator.playPing();

    set({
      uploadingAttachment: true,
      replyingTo: undefined,
    });

    get().pushComment({
      id: fakeMessageId,
      updatedAt: get().getCurrentTime(),
      createdAt: get().getCurrentTime(),
      session: session,
      type: "SHARED_COMMENT",
      data: {
        content: sanitizeHtmlHelper(message),
      },
      // Full local object so the optimistic bubble renders its quote right away.
      replyTo: replyingTo,
      sending: true,
    });

    const updateError = () => {
      // Set message sending as failed.
      const updatedComments = get().currentComments.map((comment) => {
        if (comment.sending) {
          comment.sending = false;
          comment.error = true;
        }
        return comment;
      });

      get().setCurrentComments(updatedComments);
    };

    try {
      var attachmentsUrls: any[] = [];
      var attachmentUploadError: string | undefined;
      set({ uploadProgress: attachments.map(() => 0) });
      for (let i = 0; i < attachments.length; i++) {
        const entry = attachments[i];
        if (entry instanceof File) {
          try {
            const fileUrl = await get().uploadFile(entry, (pct) => {
              get().setUploadProgress(i, pct);
            });
            attachmentsUrls.push({
              url: fileUrl,
              name: entry.name,
              type: entry.type,
            });
          } catch (exp) {
            attachmentUploadError =
              exp instanceof Error
                ? exp.message
                : CONVERSATION_ATTACHMENT_SIZE_ERROR_MESSAGE;
            alert(attachmentUploadError);
          }
        } else {
          attachmentsUrls.push({
            url: entry.url,
            name: entry.name,
            type: entry.type,
          });
        }
      }
      set({ uploadProgress: [] });

      if (attachmentsUrls.length <= 0 && message === defaultAttachmentMessage) {
        try {
          set({
            uploadingAttachment: false,
          });
          get().setCurrentComments(
            get().currentComments.map((comment) => {
              if (comment.sending) {
                comment.sending = false;
                comment.data.content = `❌ ${
                  attachmentUploadError ||
                  CONVERSATION_ATTACHMENT_SIZE_ERROR_MESSAGE
                }`;
              }
              return comment;
            }),
          );
        } catch (exp) {}

        return;
      }

      const token = await validateRecaptchaAction("sendchatmessage");

      const appMode = useRouterStore.getState().appMode;
      const source =
        appMode === AppMode.CHATBAR
          ? "chatbar"
          : appMode === AppMode.WIDGET
            ? "widget"
            : undefined;

      try {
        await HttpMessageService.getInstance().create({
          data: {
            clientMessageId,
            comment: message,
            attachments: attachmentsUrls,
            recaptchaToken: token,
            ticketShareToken: currentTicket.shareToken,
            ...(source ? { source } : {}),
            ...(replyingTo?.id ? { replyTo: replyingTo.id } : {}),
          },
        });

        set({
          uploadingAttachment: false,
        });
        get().setCurrentComments(
          get().currentComments.map((comment) => {
            if (comment.id === fakeMessageId) {
              comment.sending = false;
              comment.attachments = attachmentsUrls;
            }
            return comment;
          }),
        );

        // Replying into a chatbar conversation makes it the latest one to reopen
        // on focus.
        if (appMode === AppMode.CHATBAR) {
          saveActiveChatbarTicket(
            useSessionStore.getState().session?.gleapId,
            currentTicket,
          );
        }
      } catch (err) {
        // Same contract as the outer catch: a failed send must not leave the
        // composer's attach button replaced by a permanent spinner (only the
        // success path above resets the flag, and this flag even survives
        // conversation switches).
        set({ uploadingAttachment: false });
        updateError();
      }
    } catch (exp) {
      // Never leave the optimistic bubble stuck on "Sending..." — if anything
      // before the request threw (e.g. attachment/recaptcha handling), surface
      // a retryable error instead of an endless spinner.
      set({ uploadingAttachment: false });
      updateError();
    }
  },
  unlockChatbarList: () => {
    set({ chatbarListUnlocked: true });
  },
  loadConversations: () => {
    // Chatbar loads its conversation list lazily — only after the panel has been
    // opened at least once (which calls unlockChatbarList). appMode is set
    // synchronously from the /chatbar path at module load (src/index.tsx), so it
    // is reliably CHATBAR before any async session-update triggers a load.
    if (
      useRouterStore.getState().appMode === AppMode.CHATBAR &&
      !get().chatbarListUnlocked
    ) {
      return;
    }

    // Stale-while-revalidate: keep the rendered conversation list while the
    // refresh runs (the Messages tab used to blank out on every widget open).
    // The fresh first page replaces it on arrival.
    set({
      previousTicketsLoaded: get().previousTickets.length > 0,
      loadingTickets: false,
      canLoadMore: true,
    });

    get().getUnreadTickets();

    get().loadMoreConversations({ skip: 0, replace: true });
  },
  getUnreadTickets: async () => {
    try {
      // Chatbar lazy gate: the unread list is only consumed by widget UI, never the
      // collapsed chatbar, so skip the fetch until the panel has been opened once.
      if (
        useRouterStore.getState().appMode === AppMode.CHATBAR &&
        !get().chatbarListUnlocked
      ) {
        return;
      }

      const { session, sdkKey } = useSessionStore.getState();
      if (!session || !sdkKey) {
        return;
      }

      // Load unread tickets. In chatbar mode, restrict to chatbar-origin tickets
      // so the badge reflects only chatbar conversations.
      const unreadFilter: Record<string, unknown> = {
        sessionNotificationsUnread: true,
      };
      if (useRouterStore.getState().appMode === AppMode.CHATBAR) {
        unreadFilter.origin = "chatbar";
      }
      const tickets = await HttpTicketService.getInstance().find({
        query: {
          filter: JSON.stringify(unreadFilter),
          limit: 10,
          skip: 0,
        },
      });

      // The conversation currently open in a visible tab is being read right
      // now — a stale or in-flight server response must not re-badge it.
      const viewedTicketId =
        document.visibilityState !== "hidden"
          ? get().currentTicket?.id
          : undefined;

      set({
        unreadTickets: (tickets ?? []).filter(
          (ticket: Ticket) => ticket.id !== viewedTicketId,
        ),
      });
    } catch (error) {
      console.error("Error getting unread tickets", error);
    }
  },
  silentRefreshTickets: () => {
    // Respect the chatbar lazy gate: don't refresh the (invisible) conversation
    // list until the panel has been opened at least once.
    if (
      useRouterStore.getState().appMode === AppMode.CHATBAR &&
      !get().chatbarListUnlocked
    ) {
      return;
    }

    get()
      .getConversations(0, 25)
      .then((data) => {
        let newTickets = [...data, ...get().previousTickets].filter(
          (v, i, a) => a.findIndex((v2) => v2.id === v.id) === i,
        );

        newTickets = cleanConversationList(newTickets);

        set({
          previousTickets: newTickets,
          previousTicketsLoaded: true,
        });
      })
      .catch(() => {});
  },
  getConversations: async (skip = 0, limit = 25) => {
    try {
      let query: any = {
        limit,
        skip,
      };

      if (get().ticketFilter.status) {
        if (get().ticketFilter.status === "OPEN") {
          query = {
            ...query,
            status: { $nin: ["CLOSED", "DONE"] },
          };
        } else {
          query = {
            ...query,
            status: get().ticketFilter.status,
          };
        }
      }

      if (useRouterStore.getState().appMode === AppMode.CHATBAR) {
        query.origin = "chatbar";
      }

      query = {
        filter: JSON.stringify({
          ...query,
        }),
      };

      const tickets = await HttpTicketService.getInstance().find({
        query,
      });

      return tickets;
    } catch (_) {
      return [];
    }
  },
  loadMoreConversations: (args) => {
    const { session, sdkKey } = useSessionStore.getState();
    if (get().loadingTickets || !get().canLoadMore || !session || !sdkKey) {
      return;
    }

    if (args?.preventLoading) {
      set({
        loadingTickets: true,
      });
    }

    const skip = args?.skip ?? get().previousTickets.length;

    get()
      .getConversations(skip, 25)
      .then((data) => {
        var newTickets: any[] = [];
        if (args?.replace) {
          // Refresh: the first page replaces the kept (stale) list wholesale —
          // an empty answer is real (no conversations) and clears it.
          newTickets = data && data.length > 0 ? [...data] : [];
        } else if (data && data.length > 0) {
          newTickets = [...get().previousTickets];
          newTickets = newTickets.filter(function (el) {
            return data.indexOf(el) < 0;
          });
          newTickets = [...newTickets, ...data];
        } else {
          newTickets = [...get().previousTickets];
        }

        newTickets = cleanConversationList(newTickets);

        set({
          previousTickets: newTickets,
          previousTicketsLoaded: true,
          loadingTickets: false,
          canLoadMore: data.length > 0,
        });
      })
      .catch(() => {});
  },
  clearUserTyping: () => {
    get().clearUserTypingTimeout();
    set({
      typingUser: undefined,
      typingSource: undefined,
      typingAgent: undefined,
    });
  },
  clearUserTypingTimeout: () => {
    const timeout = get().userTypingTimeout;
    if (timeout) {
      clearTimeout(timeout);
    }
  },
  setUserTyping: (
    user: any,
    source: "local" | "server" = "local",
    agent?: { agentName?: string; agentAvatarUrl?: string },
  ) => {
    get().clearUserTypingTimeout();
    set({
      typingUser: user,
      typingSource: source,
      typingAgent: agent?.agentName ? agent : undefined,
      userTypingTimeout: setTimeout(() => {
        set({
          userTypingTimeout: undefined,
          typingUser: undefined,
          typingSource: undefined,
          typingAgent: undefined,
        });
      }, 20000),
    });
  },
  handleWebSocketEvent: (webSocketEventData: WebSocketEventData) => {
    switch (webSocketEventData.action) {
      case WebSocketEvent.MESSAGE_CREATED:
        // Every voice turn is mirrored into the thread as a created comment —
        // pinging each one mid-call is noise over the actual conversation.
        if (!shouldMuteWidgetSounds()) {
          Communicator.playPing();
        }
        if (get().currentTicket?.id !== webSocketEventData.parentID) return;
        if (!CommunicationManager.isWidgetOpen) return;
        // No clearUserTyping here: applyCreatedComment owns it, so the typing
        // indicator survives until the fetched message actually renders.
        applyCreatedComment(
          webSocketEventData.actionID,
          webSocketEventData.parentID,
          get,
          set,
        );
        break;
      case WebSocketEvent.MESSAGE_UPDATED:
        if (get().currentTicket?.id !== webSocketEventData.parentID) {
          return;
        }

        if (
          webSocketEventData?.actionData?.type === "ACTION" &&
          webSocketEventData?.actionData?.data?.type === "video_call"
        ) {
          get().setCurrentComments(
            get().currentComments.map((comment) => {
              if (comment.id === webSocketEventData.actionID) {
                return {
                  ...comment,
                  ...webSocketEventData.actionData,
                };
              }
              return comment;
            }),
          );
        } else if (webSocketEventData?.actionData?.data?.widget) {
          // One of Kai's cards was answered: take the server's widget state.
          // The server can write a card twice back to back (booking, feature
          // requests); the comment's `updatedAt` orders the writes, so an
          // older one arriving late can't roll the card back.
          const incomingUpdatedAt = webSocketEventData.actionData.updatedAt;
          const incomingAt = toTime(incomingUpdatedAt);
          get().setCurrentComments(
            get().currentComments.map((comment) => {
              if (comment.id !== webSocketEventData.actionID) {
                return comment;
              }
              const appliedAt = toTime(
                comment.widgetUpdatedAt ?? comment.updatedAt,
              );
              if (
                incomingAt !== undefined &&
                appliedAt !== undefined &&
                incomingAt < appliedAt
              ) {
                return comment;
              }
              return {
                ...comment,
                ...(incomingAt !== undefined
                  ? { widgetUpdatedAt: incomingUpdatedAt }
                  : {}),
                data: {
                  ...(comment.data ?? {}),
                  widget: webSocketEventData.actionData.data.widget,
                },
              };
            }),
          );
        } else if (webSocketEventData?.actionData?.data?.captureRequest) {
          // Capture card status change (completed / skipped / cancelled /
          // expired, or in_progress while a device works on it). Merged, so
          // the rendered comment keeps anything the payload leaves out.
          get().setCurrentComments(
            get().currentComments.map((comment) =>
              comment.id === webSocketEventData.actionID
                ? mergeCaptureCommentUpdate(
                    comment,
                    webSocketEventData.actionData,
                  )
                : comment,
            ),
          );
        }
        break;
      case WebSocketEvent.MESSAGE_DELETED:
        if (get().currentTicket?.id !== webSocketEventData.parentID) {
          return;
        }

        get().setCurrentComments(
          get().currentComments.filter(
            (comment) => comment.id !== webSocketEventData.actionID,
          ),
        );

        break;
      case WebSocketEvent.BUG_TYPING:
        if (
          get().currentTicket?.id === webSocketEventData.actionData?.ticketId
        ) {
          if (
            webSocketEventData.actionData?.typing &&
            webSocketEventData.actionData?.user
          ) {
            get().setUserTyping(webSocketEventData.actionData?.user, "server", {
              agentName: webSocketEventData.actionData?.agentName,
              agentAvatarUrl: webSocketEventData.actionData?.agentAvatarUrl,
            });
          } else {
            get().clearUserTyping();
          }
        }
        break;

      case WebSocketEvent.BOT_WRITTING:
        const conversationId = webSocketEventData?.actionData?.conversationId;
        if (
          get().currentTicket?.id === conversationId &&
          get().canSetBotWrittingText
        ) {
          set({
            botWrittingText: webSocketEventData?.actionData?.message,
            botWrittingIsThinking:
              webSocketEventData?.actionData?.isThinking === true,
            botWrittingState: webSocketEventData?.actionData?.state,
          });
          armBotWrittingWatchdog(get, set);
        }
        break;

      case WebSocketEvent.BOT_TOOL:
        const toolName = webSocketEventData?.actionData?.name;
        if (toolName && toolName.length > 0) {
          // The Kai avatar shows the cog for a beat.
          set({ botToolAt: Date.now() });
          // Send to communicator.
          CommunicationManager.getInstance().sendMessage({
            name: "tool-execution",
            data: {
              name: toolName,
              params: webSocketEventData?.actionData?.params ?? {},
              result:
                webSocketEventData?.actionData?.result ??
                webSocketEventData?.actionData?.message ??
                "",
              message: webSocketEventData?.actionData?.message ?? "",
            },
          });
        }
        break;

      case WebSocketEvent.CUSTOMACTION_RUN:
        Communicator.runCustomAction(
          webSocketEventData?.actionData?.actionName,
          webSocketEventData?.actionData?.shareToken,
        );

        if (webSocketEventData?.actionData?.closeWidget ?? true) {
          Communicator.closeWidget();
        }
        break;

      case WebSocketEvent.BUG_CREATED:
        // Reload conversations.
        get().silentRefreshTickets();
        break;

      case WebSocketEvent.BUG_UPDATED:
        if (webSocketEventData?.actionData?.update) {
          get().updateCurrentTicket(
            webSocketEventData?.actionID,
            webSocketEventData?.actionData?.update,
          );

          // If the cached "reopen on focus" conversation just got resolved, drop
          // it so a later focus falls back to the blank bar instead of reopening
          // a DONE/CLOSED thread.
          const cachedGleapId = useSessionStore.getState().session?.gleapId;
          const cachedReopen = getActiveChatbarTicket(cachedGleapId);
          if (
            cachedReopen &&
            cachedReopen.id === webSocketEventData?.actionID &&
            !isReopenableStatus(webSocketEventData?.actionData?.update?.status)
          ) {
            clearActiveChatbarTicket(cachedGleapId);
          }

          // Check if ticket is unread.
          if (
            webSocketEventData?.actionData?.update
              ?.sessionNotificationsUnread &&
            !get().currentTicket
          ) {
            get().getUnreadTickets();
          }
        }
        break;

      case WebSocketEvent.BOT_OPENLINK:
        if (webSocketEventData?.actionData?.href) {
          Communicator.openURL(
            webSocketEventData?.actionData?.href,
            webSocketEventData?.actionData?.newTab,
            // The server already filled the workflow's variables.
            { placeholders: false },
          );
        }
        break;

      case WebSocketEvent.BOT_OPENTICKET: {
        const targetShareToken = webSocketEventData?.actionData?.shareToken;
        if (targetShareToken) {
          // Switch the customer to their existing conversation. Replace history
          // (setPage) so "back" can't return to the now-archived new conversation.
          redirectedToShareTokenAt = Date.now();
          useRouterStore
            .getState()
            .setPage(
              "conversation",
              { shareToken: targetShareToken },
              AppMode.WIDGET,
            );
          get().loadCurrentTicket(targetShareToken);
        }
        break;
      }

      case WebSocketEvent.BOT_STARTFEEDBACKFLOW:
        const flow = webSocketEventData?.actionData?.flow;
        const preFillData = webSocketEventData?.actionData?.preFillData;
        if (webSocketEventData?.actionData?.flow) {
          // A voice action pushes this while the call overlay may still be up
          // (ended screen) — defer the navigation until it unmounts so the
          // form appears the moment the customer sees the app again.
          runOrDeferUntilCallOver(() => {
            useFormStore.setState({
              feedbackFlow: flow,
              isSurveyFlow: false,
              // Prefill from the spoken conversation (e.g. the bug
              // description Kai collected) — same shape the in-thread action
              // button uses (aiActionHandler).
              ...(preFillData && typeof preFillData === "object"
                ? { preFillData }
                : {}),
            });
            useRouterStore.getState().setPage(
              "flow",
              {
                flowId: flow,
                // The workflow's conversation (#184947).
                handoffTicketId: webSocketEventData?.parentID,
              },
              AppMode.WIDGET,
            );
            useAppViewStore.getState().reset();
          });
        }
        break;

      default:
        break;
    }
  },
  // These read the comment BEFORE the tail (the tail is the trailing action
  // comment) — the diagnostic-logs notice is a system line, not part of that
  // shape, so it is left out.
  lastMessageHasContextAnswer: () => {
    try {
      const comments = get().currentComments.filter(
        (comment) => !isCaptureLogsNotice(comment),
      );
      const lastComment = comments[comments.length - 2];

      return lastComment?.data?.contextMissing ?? false;
    } catch (exp) {}

    return true;
  },
  lastMessageIsAIAnswer: () => {
    try {
      const comments = get().currentComments.filter(
        (comment) => !isCaptureLogsNotice(comment),
      );
      const lastComment = comments[comments.length - 2];

      return (
        lastComment?.type === "BOT" && lastComment?.data?.answerBot === true
      );
    } catch (exp) {}

    return false;
  },
  getBotResponseCount: () => {
    return get().currentComments.filter(
      (comment) =>
        comment.type === "BOT" &&
        comment?.data?.answerBot === true &&
        !isCaptureLogsNotice(comment),
    ).length;
  },
}));
